import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { parseDateWithoutTimezone } from 'src/utils/convertDateToSelectedTimeZone';

type PayerInfo = {
  uuid: string;
  payerPlanId: string;
  payerType: string;
  payerName?: string;
  startDate: string;
  endDate: string | null;
  programType?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (payerToUpdate: PayerInfo, newEndDate: string | null) => void;
  mostRecentPayer: PayerInfo | null;
  eocMinEnd?: Date | null;
};

const formatDateForDisplay = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  try {
    const date = new Date(
      new Date(dateStr).valueOf() + new Date(dateStr).getTimezoneOffset() * 60 * 1000
    );
    return format(date, 'MMM dd, yyyy');
  } catch {
    return dateStr;
  }
};

const OpenEocPayerModal = ({ open, onClose, onConfirm, mostRecentPayer, eocMinEnd }: Props) => {
  const [option, setOption] = useState<'new-end-date' | 'open-indefinitely'>('open-indefinitely');
  const [newEndDate, setNewEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (open) {
      setOption('open-indefinitely');
      setNewEndDate(new Date());
    }
  }, [open]);

  const minPayerEndDate = useMemo(() => {
    if (!mostRecentPayer) return undefined;
    const payerStart = parseDateWithoutTimezone(mostRecentPayer.startDate?.split('T')[0]);
    if (!eocMinEnd) return payerStart;
    if (!payerStart) return eocMinEnd;
    return payerStart > eocMinEnd ? payerStart : eocMinEnd;
  }, [mostRecentPayer, eocMinEnd]);

  const handleConfirm = () => {
    if (!mostRecentPayer) return;

    if (option === 'new-end-date' && newEndDate) {
      const day = newEndDate.getDate().toString().padStart(2, '0');
      const month = (newEndDate.getMonth() + 1).toString().padStart(2, '0');
      const year = newEndDate.getFullYear();
      onConfirm(mostRecentPayer, `${year}-${month}-${day}`);
    } else {
      onConfirm(mostRecentPayer, null);
    }
  };

  const getPayerTypeDisplay = (payer: PayerInfo) => {
    return payer.payerType?.replaceAll('_', ' ') || payer.payerType;
  };

  if (!mostRecentPayer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1.5,
            color: '#111927',
          }}
        >
          Reopen Payer Coverage
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography
            variant="body2"
            sx={{ color: '#6C737F' }}
          >
            All payers for this patient are currently closed. Would you like to update the most
            recent payer?
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111927' }}
            >
              {getPayerTypeDisplay(mostRecentPayer)} - {mostRecentPayer.payerName || ''}
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{ mt: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: '#6C737F' }}
                >
                  Start Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#111927' }}
                >
                  {formatDateForDisplay(mostRecentPayer.startDate) || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: '#6C737F' }}
                >
                  End Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#111927' }}
                >
                  {formatDateForDisplay(mostRecentPayer.endDate) || '-'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <RadioGroup
            value={option}
            onChange={(e) => setOption(e.target.value as 'new-end-date' | 'open-indefinitely')}
          >
            <FormControlLabel
              value="open-indefinitely"
              control={<Radio />}
              label={
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                >
                  Open indefinitely (remove end date)
                </Typography>
              }
            />
            <FormControlLabel
              value="new-end-date"
              control={<Radio />}
              label={
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                >
                  Set a new end date
                </Typography>
              }
            />
          </RadioGroup>

          {option === 'new-end-date' && (
            <DatePicker
              label="New End Date"
              value={newEndDate}
              onChange={(date) => setNewEndDate(date)}
              minDate={minPayerEndDate ?? undefined}
              format="MMM dd, yyyy"
              sx={{ width: '100%' }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px 24px 24px' }}>
        <Button
          variant="text"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={option === 'new-end-date' && !newEndDate}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenEocPayerModal;
